/**
 * @ngdoc service
 * @name userApiService
 * @module flowingly.services
 *
 * @description A service responsible solely for fetching users data
 *
 * ## Notes
 * No formatting to be performed here.
 *
 */

import { IUserSummary } from '@Shared.Angular/@types/core/contracts/queryModel/user/userSummary';
import IUserBasicInfo from '@Shared.Angular/@types/core/contracts/queryModel/user/UserBasicInfo';
import angular from 'angular';
import IUserImportRequest from '@Shared.Angular/@types/core/contracts/queryModel/user/userImportRequest';
import { ExternalUserValidationResult } from '@Shared.Angular/@types/core/contracts/queryModel/user/ExternalUserValidationResult';
import { ISearchUserResultModel } from '@Shared.Angular/@types/core/contracts/queryModel/user/searchUserResultModel';
import { Guid } from '@Shared.Angular/@types/guid';
import { IUserBasicInfoPaginationModel } from '@Shared.Angular/@types/core/contracts/queryModel/user/UserBasicInfoPaginationModel';
import IUserDetail from '@Shared.Angular/@types/core/contracts/queryModel/user/userDetail';
import { ISearchBasicUserResultModel } from '@Shared.Angular/@types/core/contracts/queryModel/user/searchBasicUserResultModel';
/// i.e. we return response.data.DataModel so the consumer can work with the data directly

angular.module('flowingly.services').factory('userApiService', userApiService);

userApiService.$inject = [
  '$http',
  'APP_CONFIG',
  'sessionService',
  'lodashService',
  'tokenService'
];

function userApiService(
  $http: angular.IHttpService,
  APP_CONFIG,
  sessionService,
  lodashService,
  tokenService
) {
  const service = {
    addUser: addUser,
    addUsers: addUsers,
    deleteUser: deleteUser,
    editUser: editUser,
    getUsers: getUsers,
    getUser: getUser,
    getUsersWithOptions: getUsersWithOptions,
    getUsersOfRole: getUsersOfRole,
    getUsersWithPermission: getUsersWithPermission,
    getUserInvolvementInFlow: getUserInvolvementInFlow,
    userLoginAudit: userLoginAudit,
    userLoginErrorAudit: userLoginErrorAudit,
    searchUsers: searchUsers,
    searchUsersCustomDB: searchUsersCustomDB,
    getLoginState: getLoginState,
    updateLoginState: updateLoginState,
    getUserProfileUsingId: getUserProfileUsingId,
    getUsersBasicInfo: getUsersBasicInfo,
    getUserBasicInfoByFieldName: getUserBasicInfoByFieldName,
    isValidExternalUser: isValidExternalUser
  };

  return service;

  //////////// Public API Methods

  function addUser(user) {
    return $http
      .post<IUserSummary>(APP_CONFIG.apiBaseUrl + 'users/', user)
      .then(function (response) {
        return response;
      });
  }

  function addUsers(users: IUserImportRequest[], noSpinner: boolean) {
    return $http
      .post<IUserImportRequest[]>(
        APP_CONFIG.apiBaseUrl + 'users/addUsers',
        users,
        {
          noSpinner: noSpinner || false
        }
      )
      .then(function (response) {
        return response.data;
      });
  }

  function getUserInvolvementInFlow(userId, flowId) {
    return $http
      .get(`${APP_CONFIG.apiBaseUrl}users/${userId}/flow/${flowId}`)
      .then((response) => {
        return response.data;
      });
  }

  function deleteUser(
    userId: Guid,
    replaceActorId: Guid | null,
    replaceManagerId: Guid | null,
    replaceCustomDatabaseActorId: Guid | null,
    replaceCategoryApproverId: Guid | null,
    replaceStepTaskUserId: Guid | null
  ) {
    return $http({
      url: APP_CONFIG.apiBaseUrl + 'users/',
      method: 'DELETE',
      data: {
        deletedUserId: userId,
        replaceActorId: replaceActorId,
        replaceManagerId: replaceManagerId,
        replaceCustomDatabaseActorId: replaceCustomDatabaseActorId,
        replaceCategoryApproverId: replaceCategoryApproverId,
        replaceStepTaskUserId: replaceStepTaskUserId
      },
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    });
  }

  function editUser(user) {
    return $http
      .put<IUserSummary>(APP_CONFIG.apiBaseUrl + 'users/', user)
      .then(function (response) {
        return response;
      });
  }

  function getUsers(noSpinner = false) {
    return $http
      .get<IUserSummary[]>(
        APP_CONFIG.apiBaseUrl +
          'users?businessId=' +
          tokenService.getTenant().id,
        { noSpinner: noSpinner }
      )
      .then(function (response) {
        return response.data;
      });
  }

  function getUser(userId: string) {
    // @TODO replace this with a better api endpoint
    // as of 18/02/2019 there is no get single Id endpoint
    const filterParams = {
      filter: {
        filters: [{ field: 'id', value: userId }]
      }
    };
    return getUsersWithOptions(filterParams).then(({ total, users }) => {
      return users.pop();
    });
  }

  /**
   * Search for {limit} amount of users
   * and optionally filter the first and last name by a term.
   *
   * @param {string} term string to filter
   * @param {number} limit amount to limit by
   * @param {boolean} noSpinner
   */
  function searchUsers({
    term = null,
    limit = 100,
    noSpinner = true,
    permissionSecureCodes = null,
    excludeInvitedUsers = false
  } = {}) {
    const url = `${APP_CONFIG.apiBaseUrl}users/searchuser`;

    const params = {
      maxNumberOfUsersToShow: limit,
      searchTerm: undefined,
      permissionSecureCodes: permissionSecureCodes,
      excludeInvitedUsers: excludeInvitedUsers
    };

    if (term) {
      params.searchTerm = term;
    }

    const options = { noSpinner, params, headers: undefined };
    const currentUser = sessionService.getUser();
    const token = tokenService.getToken();
    if (!currentUser && token) {
      options.headers = { Authorization: 'Bearer ' + token };
    }

    return $http
      .get<IUserBasicInfoPaginationModel>(url, options)
      .then(function (response) {
        return response.data.users;
      });
  }

  function searchUsersCustomDB({
    clientId = null,
    limit = 200,
    noSpinner = true
  } = {}) {
    const url = `${APP_CONFIG.apiBaseUrl}users/searchcustomdb`;

    const params = {
      clientId: clientId || tokenService.getTenant().id,
      maxNumberOfUsersToShow: limit
    };

    const options = { noSpinner, params, headers: undefined };

    const currentUser = sessionService.getUser();
    const token = tokenService.getToken();
    if (!currentUser && token) {
      options.headers = { Authorization: 'Bearer ' + token };
    }

    return $http
      .get<IUserBasicInfoPaginationModel>(url, options)
      .then(function (response) {
        return response.data.users;
      });
  }

  function getUsersWithOptions(options) {
    const defaultOpts = {
      take: 100,
      skip: 0,
      page: 1,
      pageSize: 100,
      group: []
    };
    options = lodashService.defaults(options, defaultOpts);
    return $http
      .post<ISearchUserResultModel>(
        APP_CONFIG.apiBaseUrl + 'users/getUsersWithOptions',
        options,
        {
          noSpinner: true
        }
      )
      .then(function (response) {
        return response.data;
      });
  }

  function getUsersOfRole(roleName, noSpinner) {
    return $http
      .get<IUserDetail[]>(
        APP_CONFIG.apiBaseUrl + 'users/usersOfRole?roleName=' + roleName,
        {
          noSpinner: noSpinner || false
        }
      )
      .then(function (response) {
        return response.data;
      });
  }

  function getUsersWithPermission(permissionCode, noSpinner = false) {
    return $http
      .get<IUserBasicInfo[]>(
        APP_CONFIG.apiBaseUrl + 'users?permissionCode=' + permissionCode,
        {
          noSpinner: noSpinner || false
        }
      )
      .then(function (response) {
        return response.data;
      });
  }

  function userLoginAudit(loginAudit) {
    return $http
      .post(APP_CONFIG.apiBaseUrl + 'account/loginAudit', loginAudit, {
        noSpinner: false
      })
      .then(function (response) {
        return response;
      });
  }

  function userLoginErrorAudit(loginAudit) {
    return $http.post(
      APP_CONFIG.apiBaseUrl + 'account/loginFailAudit',
      loginAudit,
      { noSpinner: false }
    );
  }

  function getLoginState() {
    return $http
      .get<boolean>(APP_CONFIG.apiBaseUrl + `account/loggedIn`)
      .then(function (response) {
        return response.data;
      });
  }

  function updateLoginState(loggedIn: boolean) {
    return $http.put<string>(
      APP_CONFIG.apiBaseUrl + `account/loggedIn/${loggedIn}`,
      null
    );
  }

  function getUserProfileUsingId(userId: string) {
    return $http
      .get<IUserSummary>(APP_CONFIG.apiBaseUrl + `users/${userId}/profile`, {
        noSpinner: true
      })
      .then(function (response) {
        return response.data;
      });
  }

  function getUsersBasicInfo(options) {
    const defaultOpts = {
      take: 100,
      skip: 0,
      page: 1,
      pageSize: 100,
      group: []
    };
    options = lodashService.defaults(options, defaultOpts);
    return $http
      .post<ISearchBasicUserResultModel>(
        APP_CONFIG.apiBaseUrl + 'users/basic',
        options,
        {
          noSpinner: true
        }
      )
      .then((response) => {
        return {
          ...response.data,
          users: response.data.users
        };
      });
  }

  function getUserBasicInfoByFieldName(userId: string, fieldName = 'id') {
    const filterParams = {
      filter: {
        filters: [{ field: fieldName, value: userId }]
      }
    };
    return getUsersBasicInfo(filterParams).then(({ total, users }) => {
      return users.pop();
    });
  }

  function isValidExternalUser(email: string) {
    return $http
      .post<ExternalUserValidationResult>(
        APP_CONFIG.apiBaseUrl + 'users/externalUser/validate',
        JSON.stringify(email)
      )
      .then(function (response) {
        return response.data;
      })
      .catch(function (error) {
        console.error('Error:', error);
      });
  }
}

export type UserApiServiceType = ReturnType<typeof userApiService>;
